import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { clearIdleRecord, createIdleSession, IDLE_ACTIVITY_EVENTS, IDLE_SIGN_OUT_MS, IDLE_WARNING_MS, idleGroup, markIdleSignOut, startIdleRecord, storedIdleState, wasIdleSignOut, type IdlePhase, type IdleSession, type IdleTiming } from "./idle";
import { openTabChannel } from "./tabs";
import type { AuthClient, AuthStatus, Session } from "./types";

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  client: AuthClient;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Sign-out takes the signed-in screens and the tokens out of the page first ("signing-out": RequireAuth shows its
 * signing-out fallback), then tells the sign-in's other tabs (they share the refresh token the sign-out revokes, see
 * tabs.ts) and lets the client revoke and leave for the logout. A page brought back from the back/forward cache after
 * that, or after its session ended, is reloaded rather than shown as it was.
 */
export function AuthProvider({ client, children }: { client: AuthClient; children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const signingOut = useRef(false);

  useEffect(() => {
    let live = true;
    client.getSession().then((s) => {
      if (!live || signingOut.current) return;
      setSession(s);
      setStatus(s ? "signed-in" : "signed-out");
    });
    const off = client.subscribe((s) => {
      if (signingOut.current) return;
      setSession(s);
      setStatus(s ? "signed-in" : "signed-out");
    });
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (signingOut.current) return window.location.reload();
      void client.getSession().then((s) => {
        if (!s) window.location.reload();
      });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      live = false;
      off();
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [client]);

  const endSession = useCallback(
    async (fromAnotherTab: boolean) => {
      if (signingOut.current) return;
      signingOut.current = true;
      setSession(null);
      setStatus("signing-out");
      if (!fromAnotherTab) {
        const channel = openTabChannel({ group: () => idleGroup() });
        channel.post({ kind: "signed-out", idle: wasIdleSignOut() });
        channel.close();
      }
      clearIdleRecord();
      await client.signOut();
    },
    [client],
  );

  // Another tab of this sign-in signed out and revoked the refresh token this tab shares: sign out here too, now.
  useEffect(() => {
    if (status !== "signed-in") return;
    const channel = openTabChannel({ group: () => idleGroup() });
    const off = channel.subscribe((m) => {
      if (m.kind !== "signed-out") return;
      if (m.idle) markIdleSignOut();
      void endSession(true);
    });
    return () => {
      off();
      channel.close();
    };
  }, [status, endSession]);

  const signIn = useCallback((returnTo?: string) => client.signIn({ returnTo }), [client]);
  const signOut = useCallback(() => endSession(false), [endSession]);
  const value = useMemo(() => ({ status, session, client, signIn, signOut }), [status, session, client, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Starts sign-in when signed out, preserving the current path as the return target. `signingOut` shows while a sign-out leaves (default: `fallback`). */
export function RequireAuth({ children, fallback = null, signingOut }: { children: ReactNode; fallback?: ReactNode; signingOut?: ReactNode }) {
  const { status, signIn } = useAuth();
  useEffect(() => {
    if (status === "signed-out") void signIn(window.location.pathname + window.location.search);
  }, [status, signIn]);
  if (status === "signing-out") return <>{signingOut ?? fallback}</>;
  if (status !== "signed-in") return <>{fallback}</>;
  return <>{children}</>;
}

/** Coarse gate on a Cognito group. The API enforces the real decision; this only avoids rendering screens the user cannot use. */
export function RequireGroup({ group, children, denied }: { group: string; children: ReactNode; denied: ReactNode }) {
  const { session } = useAuth();
  if (!session?.claims.groups.includes(group)) return <>{denied}</>;
  return <>{children}</>;
}

/** Drives the /auth/callback route. Renders nothing itself; the app decides what to show for each state. */
export function useAuthCallback(navigate: (to: string) => void): { state: "working" | "done" | "error"; error?: string } {
  const { client } = useAuth();
  const [state, setState] = useState<{ state: "working" | "done" | "error"; error?: string }>({ state: "working" });
  useEffect(() => {
    let live = true;
    client
      .handleCallback()
      .then(({ returnTo }) => {
        if (!live) return;
        // A new sign-in: a new tab group and a fresh idle clock (idle.ts, tabs.ts).
        startIdleRecord();
        setState({ state: "done" });
        navigate(returnTo);
      })
      .catch((e: unknown) => {
        if (live) setState({ state: "error", error: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      live = false;
    };
    // The callback runs once for the URL the page loaded with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);
  return state;
}

export interface IdleSignOut {
  /** "warning" while the warning should show; "expired" once the sign-out has started (or is settling), and from the first render when the stored clock had already run out. */
  phase: IdlePhase;
  /** Whole seconds until the sign-out; meaningful during the warning. */
  secondsLeft: number;
  /** The warning's "Stay signed in". During the warning only this resets the clock; other input does not. */
  staySignedIn: () => void;
}

/**
 * Signs the user out after `IDLE_SIGN_OUT_MS` without activity (pointer, keyboard, touch, scroll) in any tab of the
 * sign-in, with a warning `IDLE_WARNING_MS` before; once the warning shows, only `staySignedIn` keeps the session.
 * Hidden time counts: the clock is wall time, rechecked every second and whenever the tab becomes visible or focused
 * again, and kept in sessionStorage, so a reload or a restored tab continues it (the first render already reports
 * "expired" when it ran out, so the app can keep protected content off the screen). The sign-in's tabs share their
 * input and holds and sign out together (idle.ts `createIdleSession`, tabs.ts). The sign-out is the provider's own
 * (screens removed, stored session cleared, refresh token revoked, the signed-out route), marked so the signed-out
 * page can say it was for inactivity. The hook renders nothing; the app draws the warning. `holdUntil` (epoch ms)
 * keeps the session until then, for work the app knows is in progress without input.
 */
export function useIdleSignOut(options: { holdUntil?: number | null; enabled?: boolean } & IdleTiming = {}): IdleSignOut {
  const { status, signOut } = useAuth();
  const enabled = (options.enabled ?? true) && status === "signed-in";
  const timeoutMs = options.timeoutMs ?? IDLE_SIGN_OUT_MS;
  const warningMs = options.warningMs ?? IDLE_WARNING_MS;
  const holdUntil = options.holdUntil ?? null;
  // Before any effect runs: a stored clock that already ran out (a reopened or restored tab) must not let the app
  // render the record even once.
  const [state, setState] = useState<{ phase: IdlePhase; msLeft: number }>(() => {
    const stored = storedIdleState({ timeoutMs, warningMs });
    return stored && stored.phase !== "active" ? { phase: stored.phase, msLeft: stored.msLeft } : { phase: "active", msLeft: timeoutMs };
  });
  const idle = useRef<IdleSession | null>(null);
  const signOutRef = useRef(signOut);
  const holdRef = useRef(holdUntil);
  useEffect(() => {
    signOutRef.current = signOut;
    holdRef.current = holdUntil;
  });

  useEffect(() => {
    if (!enabled) return;
    const channel = openTabChannel({ group: () => idleGroup() });
    const s = createIdleSession({
      timeoutMs,
      warningMs,
      hold: holdRef.current,
      channel,
      onChange: (next) => setState({ phase: next.phase, msLeft: next.msLeft }),
      onExpire: () => {
        markIdleSignOut();
        void signOutRef.current();
      },
    });
    idle.current = s;
    setState({ phase: s.state().phase, msLeft: s.state().msLeft });
    const onActivity = (e: Event) => {
      // Browsers send zero-movement pointer moves when content shifts under a still mouse; those are not a person.
      if (e.type === "pointermove" && !(e as PointerEvent).movementX && !(e as PointerEvent).movementY) return;
      s.activity();
    };
    const recheck = () => void s.check();
    const listen = { capture: true, passive: true } as const;
    for (const type of IDLE_ACTIVITY_EVENTS) document.addEventListener(type, onActivity, listen);
    document.addEventListener("visibilitychange", recheck);
    document.addEventListener("resume", recheck);
    window.addEventListener("focus", recheck);
    window.addEventListener("pageshow", recheck);
    const tick = setInterval(recheck, 1000);
    return () => {
      for (const type of IDLE_ACTIVITY_EVENTS) document.removeEventListener(type, onActivity, listen);
      document.removeEventListener("visibilitychange", recheck);
      document.removeEventListener("resume", recheck);
      window.removeEventListener("focus", recheck);
      window.removeEventListener("pageshow", recheck);
      clearInterval(tick);
      s.close();
      channel.close();
      idle.current = null;
    };
  }, [enabled, timeoutMs, warningMs]);

  useEffect(() => {
    idle.current?.setHold(holdUntil);
  }, [holdUntil]);

  const staySignedIn = useCallback(() => idle.current?.stay(), []);
  return { phase: enabled ? state.phase : "active", secondsLeft: Math.ceil(state.msLeft / 1000), staySignedIn };
}
